import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Handshake, ArrowRight, Building2, Mail, Lock, User, Phone, Globe, Users } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function PartnerLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check URL param for initial tab
  const initialTab = searchParams.get('tab') === 'register' ? 'register' : 'login';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Register form
  const [registerData, setRegisterData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    website: '',
    business_type: '',
    hero_partner: false,
    hero_account_number: '',
    estimated_customers: ''
  });

  // Set page title
  useEffect(() => {
    document.title = "Connect | Partner Portal";
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/partner/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      localStorage.setItem('partner_token', response.data.access_token);
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
      toast.success('Welcome back!');
      navigate('/partner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/partner/register`, {
        company_name: registerData.company_name,
        contact_name: registerData.contact_name,
        email: registerData.email,
        password: registerData.password,
        phone: registerData.phone || null,
        website: registerData.website || null,
        business_type: registerData.business_type || null,
        hero_partner: registerData.hero_partner,
        hero_account_number: registerData.hero_partner ? registerData.hero_account_number : null,
        estimated_customers: registerData.estimated_customers ? parseInt(registerData.estimated_customers) : null
      });
      
      localStorage.setItem('partner_token', response.data.access_token);
      localStorage.setItem('partner', JSON.stringify(response.data.partner));
      toast.success('Welcome to the Partner Program!');
      navigate('/partner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/connect-logo.png" alt="CONNECT" className="w-12 h-12 rounded-xl" />
            <div className="text-left">
              <h1 className="font-bold text-xl">CONNECT</h1>
              <p className="text-xs text-muted-foreground">Partner Portal</p>
            </div>
          </Link>
          <h2 className="text-2xl font-bold text-foreground">Partner Program</h2>
          <p className="text-muted-foreground mt-2">
            Wholesale pricing for reselling CONNECT to your customers
          </p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="partner-login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="partner-register-tab">Become a Partner</TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login">
              <CardHeader>
                <CardTitle>Partner Sign In</CardTitle>
                <CardDescription>Access your partner dashboard</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@company.com"
                        className="pl-10"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                        data-testid="partner-login-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="Your password"
                        className="pl-10"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        data-testid="partner-login-password"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="partner-login-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
            
            {/* Register Tab */}
            <TabsContent value="register">
              <CardHeader>
                <CardTitle>Become a Partner</CardTitle>
                <CardDescription>Join our partner program and start earning</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="company_name"
                          placeholder="Acme IT Services"
                          className="pl-10"
                          value={registerData.company_name}
                          onChange={(e) => setRegisterData({...registerData, company_name: e.target.value})}
                          required
                          data-testid="partner-company-name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Your Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="contact_name"
                          placeholder="John Smith"
                          className="pl-10"
                          value={registerData.contact_name}
                          onChange={(e) => setRegisterData({...registerData, contact_name: e.target.value})}
                          required
                          data-testid="partner-contact-name"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="you@company.com"
                        className="pl-10"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        required
                        data-testid="partner-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password *</Label>
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="Min 8 characters"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                        data-testid="partner-password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password *</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirm password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                        required
                        data-testid="partner-confirm-password"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          placeholder="+64 21 123 4567"
                          className="pl-10"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="website"
                          placeholder="www.company.com"
                          className="pl-10"
                          value={registerData.website}
                          onChange={(e) => setRegisterData({...registerData, website: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_type">Business Type</Label>
                      <Select 
                        value={registerData.business_type} 
                        onValueChange={(value) => setRegisterData({...registerData, business_type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="msp">Managed Service Provider</SelectItem>
                          <SelectItem value="it_consultant">IT Consultant</SelectItem>
                          <SelectItem value="telco_reseller">Telco Reseller</SelectItem>
                          <SelectItem value="var">Value Added Reseller</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estimated_customers">Est. Customers</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="estimated_customers"
                          type="number"
                          placeholder="10"
                          className="pl-10"
                          value={registerData.estimated_customers}
                          onChange={(e) => setRegisterData({...registerData, estimated_customers: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="hero_partner"
                      checked={registerData.hero_partner}
                      onCheckedChange={(checked) => setRegisterData({...registerData, hero_partner: checked})}
                    />
                    <Label htmlFor="hero_partner" className="text-sm font-normal cursor-pointer">
                      I am an existing Hero Internet partner
                    </Label>
                  </div>
                  
                  {registerData.hero_partner && (
                    <div className="space-y-2">
                      <Label htmlFor="hero_account">Hero Account Number</Label>
                      <Input
                        id="hero_account"
                        placeholder="e.g., 83020001"
                        value={registerData.hero_account_number}
                        onChange={(e) => setRegisterData({...registerData, hero_account_number: e.target.value})}
                      />
                    </div>
                  )}
                  
                  <Button type="submit" className="w-full" disabled={loading} data-testid="partner-register-btn">
                    {loading ? 'Creating account...' : 'Join Partner Program'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>Looking to use CONNECT for your business?</p>
          <Link to="/login" className="text-primary hover:underline">
            Sign up as a customer instead
          </Link>
        </div>
      </div>
    </div>
  );
}
